import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import AxiosConfig from '../../../../config/AxiosConfig';
import GenerateModeration from './ActionPane/Anwsers/GenerateModeration.jsx';

const useTicketQualification = (ticket, mutateTicket) => {
  const profile = useSelector((state) => state?.login?.profile);
  const services = useSelector((state) => state?.static?.services);
  const ticketTypes = useSelector((state) => state?.static?.ticketTypes);

  const [selectedService, setSelectedService] = useState(null);
  const [selectedTicketType, setSelectedTicketType] = useState(null);
  const [shouldModerateTicket, setShouldModerateTicket] = useState(false);
  const [askForModeration, setAskForModeration] = useState(false);
  const [moderationData, setModerationData] = useState(null);

  const isSuperAdmin = profile?.roles?.some(
    (role) => role?.name === 'Super-Administrateur'
  );

  const resetTicketTypeOnServiceChange = useCallback(
    (newService) => {
      if (selectedService?._id !== newService?._id) {
        setSelectedService(newService);
        setSelectedTicketType(null);
      }
    },
    [selectedService]
  );

  const handleShouldModerate = useCallback(() => {
    if (
      selectedService?._id !== ticket?.service?._id ||
      selectedTicketType?._id !== ticket?.ticketType?._id
    ) {
      setShouldModerateTicket(true);
    } else {
      setShouldModerateTicket(false);
    }
  }, [selectedService, selectedTicketType, ticket]);

  const handleServiceChange = (e, newService) => {
    resetTicketTypeOnServiceChange(newService);
    handleShouldModerate();
  };

  const handleTicketTypeChange = (e, newTicketType) => {
    setSelectedTicketType(newTicketType);
    handleShouldModerate();
  };

  const handleConfirmQualification = () => {
    AxiosConfig.create('post', `/tickets/${ticket?._id}/confirmQualification`)
      .call()
      .exit(() => mutateTicket())
      .catch((err) => console.error(err));
  };

  const handleModerateQualification = () => {
    if (shouldModerateTicket) {
      setModerationData({
        content: `Initial : ${ticket?.service?.code}-${
          ticket?.ticketType?.name
        }.\nSuggéré : ${
          services?.find((service) => service?._id === selectedService?._id)
            ?.code
        }-${
          ticketTypes?.find(
            (ticketType) => ticketType?._id === selectedTicketType?._id
          )?.name
        }.\n`,
        service: selectedService?._id,
        ticketType: selectedTicketType?._id,
      });
    }
    setAskForModeration(true);
  };

  const handleCloseModeration = () => {
    setAskForModeration(false);
    setModerationData(null);
  };

  const hasValidSelection = useMemo(
    () => selectedService !== null && selectedTicketType !== null,
    [selectedService, selectedTicketType]
  );

  const hasMPCode = useMemo(
    () => selectedService?.code === 'MP' || selectedTicketType?.code === 'MP',
    [selectedService, selectedTicketType]
  );

  const validationDisabled = useMemo(
    () =>
      !hasValidSelection ||
      !ticket?.assignedTo ||
      hasMPCode ||
      ticket?.toModerate,
    [hasValidSelection, ticket?.assignedTo, hasMPCode, ticket?.toModerate]
  );

  const canValidate = useMemo(
    () =>
      isSuperAdmin
        ? !validationDisabled
        : !validationDisabled && !ticket?.toModerate && !shouldModerateTicket,
    [isSuperAdmin, validationDisabled, ticket?.toModerate, shouldModerateTicket]
  );

  const disabledReason = useMemo(() => {
    if (!ticket?.assignedTo) {
      return 'Le ticket doit être assigné pour pouvoir qualifier.';
    }
    if (!selectedService || !selectedTicketType) {
      return 'Veuillez sélectionner un service et un type de ticket.';
    }
    if (hasMPCode) {
      return 'Le code MP n’est pas autorisé pour cette qualification.';
    }
    if (ticket?.toModerate) {
      return 'Ce ticket est en attente de modération.';
    }
    if (!isSuperAdmin && shouldModerateTicket) {
      return 'Merci de modérer la qualification avant de valider.';
    }
    return '';
  }, [
    ticket?.assignedTo,
    selectedService,
    selectedTicketType,
    hasMPCode,
    ticket?.toModerate,
    isSuperAdmin,
    shouldModerateTicket,
  ]);

  useEffect(() => {
    setSelectedService(ticket?.service);
    setSelectedTicketType(ticket?.ticketType);
  }, [ticket]);

  return {
    services,
    ticketTypes,
    selectedService,
    selectedTicketType,
    shouldModerateTicket,
    askForModeration,
    moderationData,
    handleServiceChange,
    handleTicketTypeChange,
    handleConfirmQualification,
    handleModerateQualification,
    handleCloseModeration,
    validationDisabled,
    canValidate,
    disabledReason,
  };
};

const ProjectTicketDetailsQualification = ({ ticket, mutateTicket }) => {
  const {
    services,
    ticketTypes,
    selectedService,
    selectedTicketType,
    _shouldModerateTicket,
    askForModeration,
    moderationData,
    handleServiceChange,
    handleTicketTypeChange,
    handleConfirmQualification,
    handleModerateQualification,
    handleCloseModeration,
    validationDisabled,
    canValidate,
    disabledReason,
  } = useTicketQualification(ticket, mutateTicket);

  const isDataLoading = !services || !ticketTypes;

  if (isDataLoading) {
    return <CircularProgress />;
  }

  return (
    <>
      {askForModeration ? (
        <GenerateModeration
          ticket={ticket}
          handleCloseModeration={handleCloseModeration}
          moderationData={moderationData}
        />
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Typography
            variant="h6"
            component="div"
            sx={{ fontWeight: 500, color: 'text.primary' }}
          >
            Valider la qualification
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
            <Autocomplete
              id="select-service"
              options={services?.filter(
                (service) => ['SCO', 'SLO', 'SAV'].indexOf(service?.code) !== -1
              )}
              getOptionLabel={(option) => `${option?.code} - ${option?.name}`}
              isOptionEqualToValue={(option, value) =>
                option?._id === value?._id
              }
              value={selectedService || null}
              onChange={handleServiceChange}
              sx={{ width: 250 }}
              disableClearable
              renderInput={(params) => (
                <TextField {...params} label="Service" />
              )}
            />
            <Autocomplete
              id="select-tickettype"
              options={ticketTypes?.filter(
                (ticketType) =>
                  ticketType?.service?._id === selectedService?._id
              )}
              getOptionLabel={(option) => option?.name}
              isOptionEqualToValue={(option, value) =>
                option?._id === value?._id
              }
              value={selectedTicketType}
              onChange={handleTicketTypeChange}
              sx={{ width: 180 }}
              disableClearable
              renderInput={(params) => <TextField {...params} label="Type" />}
            />
            <Button
              variant="contained"
              onClick={handleModerateQualification}
              color="warning"
              disabled={validationDisabled}
              size="small"
            >
              Modérer
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmQualification}
              color="success"
              disabled={!canValidate}
              size="small"
              sx={{ mt: 1 }}
            >
              Valider
            </Button>
          </Box>
          {disabledReason && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {disabledReason}
            </Typography>
          )}
        </Box>
      )}
    </>
  );
};

export default ProjectTicketDetailsQualification;
